/**
 * RFM Segment Map Component
 *
 * Visual representation of the 11 RFM customer segments showing:
 * - Segment boundaries on R-F grid
 * - Color-coded segments matching business value
 * - Interactive segment selection
 * - Actual customer counts from backend aggregation
 *
 * IMPORTANT - Single Source of Truth:
 * - Customer counts come from `segments` prop (backend API aggregation)
 * - Does NOT count from customer arrays to avoid inconsistency
 * - Ensures "กลุ่มลูกค้า" cards and "แผนที่กลุ่มลูกค้า" show same numbers
 *
 * Based on standard RFM segmentation model
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import type { RFMSegment, CustomerRFMData, RFMSegmentData } from "@/shared/types/rfm-analytics";
import { SEGMENT_MAP_TRANSLATIONS, MATRIX_TRANSLATIONS, SEGMENT_DESCRIPTIONS } from "@/shared/lib/rfm-translations";

interface RFMSegmentMapProps {
  customers?: CustomerRFMData[];  // Optional: For visual validation (if provided)
  segments: RFMSegmentData[];     // Required: Actual segment data with counts from backend
  onSegmentClick?: (segment: RFMSegment) => void;
}

interface SegmentBox {
  segment: RFMSegment;
  recencyRange: [number, number]; // Min-Max recency score (1-10)
  frequencyRange: [number, number]; // Min-Max frequency score (1-10)
  color: string;
  label: string;
}

// Define segment boundaries based on RFM model
const segmentBoxes: SegmentBox[] = [
  // Top tier - Champions (R[9-10], F[7-10])
  {
    segment: "Champions",
    recencyRange: [9, 10],
    frequencyRange: [7, 10],
    color: "bg-purple-600 text-white",
    label: "Champions"
  },

  // High-Value - Loyal Customers (R[7-8], F[7-10])
  {
    segment: "Loyal Customers",
    recencyRange: [7, 8],
    frequencyRange: [7, 10],
    color: "bg-purple-400 text-white",
    label: "Loyal"
  },

  // High-Value At Risk - Can't Lose Them (R[1-4], F[9-10])
  {
    segment: "Can't Lose Them",
    recencyRange: [1, 4],
    frequencyRange: [9, 10],
    color: "bg-red-600 text-white",
    label: "Can't Lose"
  },

  // Mid-Value - Potential Loyalists (R[7-10], F[5-6])
  {
    segment: "Potential Loyalists",
    recencyRange: [7, 10],
    frequencyRange: [5, 6],
    color: "bg-cyan-400 text-white",
    label: "Potential"
  },

  // Mid-Value - Need Attention (R[5-6], F[5-10])
  {
    segment: "Need Attention",
    recencyRange: [5, 6],
    frequencyRange: [5, 10],  // Visual grid position (not classification)
    color: "bg-indigo-400 text-white",
    label: "Need Attn"
  },

  // At Risk (R[1-4], F[5-8])
  {
    segment: "At Risk",
    recencyRange: [1, 4],
    frequencyRange: [5, 8],
    color: "bg-red-400 text-white",
    label: "At Risk"
  },

  // New Customers (R[9-10], F[1-4])
  {
    segment: "New Customers",
    recencyRange: [9, 10],
    frequencyRange: [1, 4],  // Visual grid position (not classification)
    color: "bg-blue-400 text-white",
    label: "New Cust."
  },

  // Promising (R[7-8], F[1-4])
  {
    segment: "Promising",
    recencyRange: [7, 8],  // Visual grid position (not classification)
    frequencyRange: [1, 4],  // Visual grid position (not classification)
    color: "bg-cyan-300",
    label: "Promising"
  },

  // About to Sleep (R[5-6], F[1-4])
  {
    segment: "About to Sleep",
    recencyRange: [5, 6],
    frequencyRange: [1, 4],
    color: "bg-indigo-600 text-white",
    label: "About To S."
  },

  // Hibernating (R[3-4], F[1-4])
  {
    segment: "Hibernating",
    recencyRange: [3, 4],
    frequencyRange: [1, 4],
    color: "bg-yellow-400",
    label: "Hibernating"
  },

  // Lost (R[1-2], F[1-4])
  {
    segment: "Lost",
    recencyRange: [1, 2],
    frequencyRange: [1, 4],
    color: "bg-gray-800 text-white",
    label: "Lost"
  }
];

export function RFMSegmentMap({ segments, onSegmentClick }: RFMSegmentMapProps) {

  // Get customer count for each segment from backend data
  const getSegmentCustomers = (box: SegmentBox): number => {
    const segmentData = segments.find(s => s.segment === box.segment);
    return segmentData?.customerCount || 0;
  };

  // Calculate total customers from all segments
  const totalCustomers = segments.reduce((sum, s) => sum + s.customerCount, 0);

  // Convert segment ranges to grid positions (0-based)
  const getGridPosition = (box: SegmentBox) => {
    // Recency: higher score = further right on X-axis
    // Frequency: higher score = higher on Y-axis (inverted for display)
    const xStart = box.recencyRange[0] - 1;
    const xEnd = box.recencyRange[1];
    const yStart = 10 - box.frequencyRange[1];
    const yEnd = 10 - box.frequencyRange[0] + 1;

    const width = xEnd - xStart;
    const height = yEnd - yStart;

    return {
      gridColumn: `${xStart + 1} / span ${width}`,
      gridRow: `${yStart + 1} / span ${height}`
    };
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{SEGMENT_MAP_TRANSLATIONS.title}</CardTitle>
          <span className="text-sm text-gray-600">{totalCustomers.toLocaleString()} ลูกค้า</span>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {SEGMENT_MAP_TRANSLATIONS.subtitle}
        </p>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col">
          {/* Axis Labels */}
          <div className="flex mb-2">
            <div className="w-12" /> {/* Spacer */}
            <div className="flex-1 text-center">
              <p className="text-xs font-medium text-gray-600">{MATRIX_TRANSLATIONS.recencyScore} →</p>
            </div>
          </div>

          {/* Main Grid Container */}
          <div className="flex">
            {/* Y-axis label */}
            <div className="flex flex-col justify-center items-center pr-2 w-10">
              <div className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-gray-600">
                {SEGMENT_MAP_TRANSLATIONS.frequencyAndMonetary} →
              </div>
            </div>

            {/* Grid */}
            <div
              className="grid gap-1 flex-1"
              style={{
                gridTemplateColumns: 'repeat(10, 1fr)',
                gridTemplateRows: 'repeat(10, 1fr)',
                minHeight: '600px'
              }}
            >
              {segmentBoxes.map((box) => {
                const position = getGridPosition(box);
                const count = getSegmentCustomers(box);
                const percentage = totalCustomers > 0 ? (count / totalCustomers) * 100 : 0;

                const description = SEGMENT_DESCRIPTIONS[box.segment as keyof typeof SEGMENT_DESCRIPTIONS];

                return (
                  <button
                    key={box.segment}
                    className={`${box.color} rounded p-3 flex flex-col items-center justify-center text-center font-semibold transition-all hover:ring-2 hover:ring-blue-500 hover:scale-105 cursor-pointer`}
                    style={position}
                    onClick={() => onSegmentClick?.(box.segment)}
                    title={`${box.segment}\n${description}\nFrequency: ${box.frequencyRange[0]}-${box.frequencyRange[1]}\nRecency: ${box.recencyRange[0]}-${box.recencyRange[1]}\n${count} customers (${percentage.toFixed(1)}%)\nClick to view customers`}
                  >
                    <span className="text-sm leading-tight font-bold">{box.label}</span>
                    {description && (
                      <span className="text-xs mt-1 opacity-85 leading-tight px-1">{description}</span>
                    )}
                    <span className="text-sm mt-2 opacity-90 font-semibold">{count.toLocaleString()}</span>
                    <span className="text-xs opacity-80">({percentage.toFixed(1)}%)</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t">
            <p className="text-xs font-medium text-gray-600 mb-2">{SEGMENT_MAP_TRANSLATIONS.segmentTiers}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-teal-600 rounded" />
                <span className="text-xs text-gray-600">{SEGMENT_MAP_TRANSLATIONS.highValue}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded" />
                <span className="text-xs text-gray-600">{SEGMENT_MAP_TRANSLATIONS.growing}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-indigo-600 rounded" />
                <span className="text-xs text-gray-600">{SEGMENT_MAP_TRANSLATIONS.atRisk}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-400 rounded" />
                <span className="text-xs text-gray-600">{SEGMENT_MAP_TRANSLATIONS.lostInactive}</span>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-gray-700">
              <strong>{SEGMENT_MAP_TRANSLATIONS.howToRead}</strong> {SEGMENT_MAP_TRANSLATIONS.howToReadDescription}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
